import React, { useState, useEffect, useRef } from 'react';
import { FiUpload, FiRefreshCw, FiCheckCircle, FiAlertCircle, FiDatabase, FiDownload, FiInfo } from 'react-icons/fi';
import api from '../services/api';
import '../components/Button.css';
import './Migratie.css';

const Migratie = () => {
  const [uploading, setUploading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedRestoreFile, setSelectedRestoreFile] = useState(null);
  const [dbStatus, setDbStatus] = useState(null);
  const [loadingDbStatus, setLoadingDbStatus] = useState(true);
  const restoreFileRef = useRef(null);
  const restoreInputRef = useRef(null);

  useEffect(() => {
    fetchStatus();
    fetchDbStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/migrate/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const fetchDbStatus = async () => {
    setLoadingDbStatus(true);
    try {
      const response = await api.get('/database/status');
      setDbStatus(response.data);
    } catch (error) {
      console.error('Error fetching database status:', error);
    } finally {
      setLoadingDbStatus(false);
    }
  };

  const handleDownloadBackup = async () => {
    try {
      const response = await api.get('/database/export', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inkoop-backup-${new Date().toISOString().split('T')[0]}.db`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Database backup gedownload!');
    } catch (error) {
      console.error('Error downloading backup:', error);
      alert('Fout bij downloaden backup: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.db')) {
        alert('Selecteer een SQLite database bestand (.db)');
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRestoreFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.db')) {
        alert('Selecteer een SQLite database backup bestand (.db)');
        if (restoreInputRef.current) restoreInputRef.current.value = '';
        return;
      }
      restoreFileRef.current = file;
      setSelectedRestoreFile(file);
    }
  };

  const handleRestore = async () => {
    const fileToRestore = selectedRestoreFile || restoreFileRef.current;
    if (!fileToRestore) {
      alert('Selecteer eerst een database backup bestand');
      return;
    }

    if (!window.confirm(
      '⚠️ Database Herstellen\n\n' +
      'Dit vervangt de huidige database met de gekozen backup.\n\n' +
      'Weet je zeker dat je door wilt gaan?'
    )) {
      return;
    }

    setRestoring(true);

    const formData = new FormData();
    formData.append('database', fileToRestore);

    try {
      const response = await api.post('/database/restore', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000
      });

      if (response.data && response.data.success) {
        const msg = response.data.reloaded
          ? 'Database hersteld. De nieuwe data is direct actief.'
          : 'Database hersteld. Vernieuw de pagina om de nieuwe data te laden.';
        alert('✅ ' + msg);
        setSelectedRestoreFile(null);
        restoreFileRef.current = null;
        if (restoreInputRef.current) restoreInputRef.current.value = '';
        fetchDbStatus();
        fetchStatus();
        if (response.data.reloaded) {
          window.location.reload();
        }
      } else {
        alert('Herstellen voltooid. Vernieuw de pagina om de nieuwe data te laden.');
        setSelectedRestoreFile(null);
        restoreFileRef.current = null;
        if (restoreInputRef.current) restoreInputRef.current.value = '';
        fetchDbStatus();
      }
    } catch (error) {
      console.error('Error restoring database:', error);
      alert('Fout bij herstellen database: ' + (error.response?.data?.error || error.message));
    } finally {
      setRestoring(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Selecteer eerst een database bestand');
      return;
    }

    if (!window.confirm(
      'Weet je zeker dat je de offline database wilt migreren?\n\n' +
      'Dit zal leveranciers en parameters overschrijven, en nieuwe producten toevoegen.\n' +
      'Bestaande producten (op basis van SKU) worden overgeslagen.'
    )) {
      return;
    }

    setUploading(true);
    setResults(null);

    const formData = new FormData();
    formData.append('database', selectedFile);

    try {
      const response = await api.post('/migrate/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 600000, // 10 minuten timeout voor grote migraties
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percentCompleted}%`);
          }
        }
      });

      setResults(response.data);
      fetchStatus(); // Refresh status
      
      if (response.data.success) {
        alert(`Migratie voltooid!\n\n` +
          `Leveranciers: ${response.data.results.find(r => r.table === 'leveranciers')?.migrated || 0}\n` +
          `Parameters: ${response.data.results.find(r => r.table === 'parameters')?.migrated || 0}\n` +
          `Producten: ${response.data.results.find(r => r.table === 'producten')?.migrated || 0} gemigreerd, ` +
          `${response.data.results.find(r => r.table === 'producten')?.skipped || 0} overgeslagen`
        );
      }
    } catch (error) {
      console.error('Error uploading database:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code
      });
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        alert('⚠️ Migratie timeout!\n\nDe migratie duurt te lang. Dit kan gebeuren bij grote databases.\n\nProbeer het opnieuw of check de Railway logs voor meer details.');
      } else if (error.response) {
        const errorMsg = error.response.data?.error || error.response.statusText || 'Onbekende fout';
        alert('Fout bij migratie: ' + errorMsg);
      } else if (error.request) {
        alert('⚠️ Network Error!\n\nGeen response van server. Mogelijk:\n- Server is niet bereikbaar\n- Timeout (te grote database)\n- CORS probleem\n\nCheck de Railway logs voor meer details.');
      } else {
        alert('Fout bij migratie: ' + error.message);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="migratie">
      <div className="page-header">
        <h1 className="page-title">
          <FiDatabase /> Data Migratie
        </h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button 
            className="btn btn-secondary" 
            onClick={handleDownloadBackup}
            disabled={!dbStatus?.database?.exists || loadingDbStatus}
            title="Download database backup"
          >
            <FiDownload /> Backup Downloaden
          </button>
        </div>
      </div>

      {/* Database Status Warning */}
      {dbStatus && dbStatus.railway && (
        <div className={`info-box ${dbStatus.warning ? 'warning-box' : 'success-box'}`} style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
            <FiInfo style={{ fontSize: '1.5rem', color: dbStatus.warning ? '#ff4444' : '#28a745', flexShrink: 0, marginTop: '0.25rem' }} />
            <div style={{ flex: 1 }}>
              <h3 style={{ marginTop: 0, marginBottom: '0.5rem', color: dbStatus.warning ? '#ff4444' : '#28a745' }}>
                {dbStatus.warning ? '⚠️ KRITIEK: Railway Volume niet ingesteld!' : '✅ Railway Volume is ingesteld'}
              </h3>
              {dbStatus.warning ? (
                <div>
                  <p style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Alle data gaat verloren bij elke deploy!
                  </p>
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Oplossing:</strong> Stel een Railway Volume in volgens de instructies in RAILWAY_VOLUME_SETUP.md
                  </p>
                  <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                    <li>Ga naar Railway → New → Volume</li>
                    <li>Koppel volume aan je service (Mount Path: /data)</li>
                    <li>Voeg environment variable toe: RAILWAY_VOLUME_MOUNT_PATH=/data</li>
                    <li>Redeploy je service</li>
                  </ul>
                  <p style={{ marginBottom: 0, fontSize: '0.9rem' }}>
                    <strong>Database locatie:</strong> {dbStatus.database.path}
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{ marginBottom: '0.5rem' }}>
                    Je database wordt opgeslagen op een persistent volume. Data blijft behouden bij deploys.
                  </p>
                  <p style={{ marginBottom: 0, fontSize: '0.9rem' }}>
                    <strong>Database locatie:</strong> {dbStatus.database.path}
                    {dbStatus.database.size > 0 && (
                      <span> ({Math.round(dbStatus.database.size / 1024)} KB)</span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="migratie-info">
        <div className="info-box">
          <FiAlertCircle className="info-icon" />
          <div>
            <h3>Migreer data van offline naar online database</h3>
            <p>
              Upload je offline database (inkoop.db) om leveranciers, parameters en producten te migreren.
            </p>
            <ul>
              <li><strong>Leveranciers:</strong> Worden overschreven (INSERT OR REPLACE)</li>
              <li><strong>Parameters:</strong> Worden overschreven (INSERT OR REPLACE)</li>
              <li><strong>Producten:</strong> Alleen nieuwe producten worden toegevoegd (bestaan al op basis van SKU worden overgeslagen)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Huidige status */}
      {status && (
        <div className="status-section">
          <h2>Huidige Data in Online Database</h2>
          <div className="status-grid">
            {status.data && status.data.map((item) => (
              <div key={item.table} className="status-card">
                <div className="status-label">{item.table}</div>
                <div className="status-value">{item.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Database Backup & Restore */}
      <div className="upload-section">
        <h2>Database Backup & Herstellen</h2>
        <div className="upload-box">
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Backup Downloaden</h3>
            <button
              className="btn btn-secondary"
              onClick={handleDownloadBackup}
              disabled={!dbStatus?.database?.exists}
            >
              <FiDownload /> Download Database Backup
            </button>
            {!dbStatus?.database?.exists && (
              <p style={{ marginTop: '0.5rem', color: '#999', fontSize: '0.9rem' }}>
                Geen database gevonden om te downloaden
              </p>
            )}
          </div>

          <div style={{ borderTop: '1px solid #ddd', paddingTop: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Database Herstellen van Backup</h3>
            <input
              ref={restoreInputRef}
              type="file"
              id="restore-file"
              accept=".db"
              onChange={handleRestoreFileSelect}
              disabled={restoring}
              style={{ display: 'none' }}
            />
            <label htmlFor="restore-file" className="file-label" style={{ cursor: restoring ? 'not-allowed' : 'pointer' }}>
              <FiUpload className="upload-icon" />
              {(selectedRestoreFile || restoreFileRef.current) ? (selectedRestoreFile || restoreFileRef.current).name : 'Selecteer database backup (.db)'}
            </label>
            
            {(selectedRestoreFile || restoreFileRef.current) && (
              <div className="file-info">
                <p>Gekozen bestand: {(selectedRestoreFile || restoreFileRef.current).name}</p>
                <p>Grootte: {((selectedRestoreFile || restoreFileRef.current).size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={handleRestore}
              disabled={(!selectedRestoreFile && !restoreFileRef.current) || restoring}
              style={{ marginTop: '1rem' }}
            >
              <FiUpload className={restoring ? 'spinning' : ''} />
              {restoring ? 'Herstellen...' : 'Database Herstellen'}
            </button>
            <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              Na herstellen wordt de nieuwe database direct geladen. De pagina kan even vernieuwen.
            </p>
          </div>
        </div>
      </div>

      {/* Upload sectie */}
      <div className="upload-section">
        <h2>Offline Database Migreren</h2>
        <div className="upload-box">
          <input
            type="file"
            id="database-file"
            accept=".db"
            onChange={handleFileSelect}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          <label htmlFor="database-file" className="file-label">
            <FiUpload className="upload-icon" />
            {selectedFile ? selectedFile.name : 'Selecteer database bestand (.db)'}
          </label>
          
          {selectedFile && (
            <div className="file-info">
              <p>Bestand: {selectedFile.name}</p>
              <p>Grootte: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            style={{ marginTop: '1rem' }}
          >
            <FiUpload className={uploading ? 'spinning' : ''} />
            {uploading ? 'Migreren... (dit kan enkele minuten duren)' : 'Start Migratie'}
          </button>
          
          {uploading && (
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
              <p style={{ margin: 0, color: '#666' }}>
                ⏳ Migratie bezig... Dit kan enkele minuten duren bij grote databases.
              </p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#999' }}>
                Blijf deze pagina open. Je krijgt een melding wanneer de migratie klaar is.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Resultaten */}
      {results && (
        <div className="results-section">
          <h2>Migratie Resultaten</h2>
          <div className="results-grid">
            {results.results && results.results.map((result, index) => (
              <div key={index} className="result-card">
                <h3>{result.table}</h3>
                <div className="result-stats">
                  <div className="stat-item">
                    <span className="stat-label">Gemigreerd:</span>
                    <span className="stat-value success">{result.migrated || 0}</span>
                  </div>
                  {result.skipped !== undefined && (
                    <div className="stat-item">
                      <span className="stat-label">Overgeslagen:</span>
                      <span className="stat-value">{result.skipped}</span>
                    </div>
                  )}
                  {result.errors > 0 && (
                    <div className="stat-item">
                      <span className="stat-label">Fouten:</span>
                      <span className="stat-value error">{result.errors}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="refresh-section">
        <button className="btn btn-secondary" onClick={fetchStatus}>
          <FiRefreshCw /> Vernieuw Status
        </button>
      </div>
    </div>
  );
};

export default Migratie;
